import { TERPENES } from './constants/labCompounds';
export { TERPENES } from './constants/labCompounds';
import React, { ChangeEvent } from 'react';
import { inferredEditorLabWeightBasis, manualLabWeightBasis, labWeightDenominator, compoundLabWeight, projectManualLabWeight, parseLabMeasurement, selectLabPercent, validLabWeightBasis, type LabWeightBasis } from './utils/labMeasurement';
import { manualCompoundValue, manualCompoundFieldValue, hasManualCompoundField, manualCompoundField, manualOverrideState } from './utils/manualLabOverrides';
import { UNITS_OF_MEASURE_OPTIONS } from './constants';
import { UNITS_OF_MEASURE } from './constants/unitsOfMeasure';
import TrashActionIcon from './components/TrashActionIcon';
import { FileField } from './components/FormFields';
import { InputRadioButton } from './components/InputRadioButton';

export const CANNABINOIDS = [
  'cbc',
  'cbca',
  'cbd',
  'cbda',
  'cbdv',
  'cbdva',
  'cbg',
  'cbga',
  'cbn',
  'cbna',
  'cbt',
  'thca',
  'delta8thc',
  'delta8thca',
  'delta9thc',
  'delta9thca',
  'delta10thc',
  'delta10thca',
  'thcv',
  'thcva',
  'totalDelta9thc',
  'totalCbd',
  'totalThc',
  'totalCannabinoids',
  'totalActiveCannabinoids',
] as const;



const AMOUNT_OPTIONS = ['Both', 'Percent', 'Weight'];

export const CANNABINOID_LABELS: Record<string, string> = {
  cbc: 'CBC',
  cbca: 'CBCa',
  cbd: 'CBD',
  cbda: 'CBDa',
  cbdv: 'CBDV',
  cbdva: 'CBDVa',
  cbg: 'CBG',
  cbga: 'CBGa',
  cbn: 'CBN',
  cbna: 'CBNa',
  cbt: 'CBT',
  thca: 'THCa',
  delta8thc: 'Δ8 THC',
  delta8thca: 'Δ8 THCa',
  delta9thc: 'Δ9 THC',
  delta9thca: 'Δ9 THCa',
  delta10thc: 'Δ10 THC',
  delta10thca: 'Δ10 THCa',
  thcv: 'THCV',
  thcva: 'THCVa',
  totalDelta9thc: 'Total Δ9 THC',
  totalCbd: 'Total CBD',
  totalThc: 'Total THC',
  totalCannabinoids: 'Total Cannabinoids',
  totalActiveCannabinoids: 'Total Active Cannabinoids',
};

export const CANNABINOIDS_LABELS = Object.values(CANNABINOID_LABELS);

export const TERPENE_LABELS: Record<string, string> = {
  aBisabolol: 'α-Bisabolol',
  aFarnesene: 'α-Farnesene',
  aMyrcene: 'α-Myrcene',
  aPhellandrene: 'α-Phellandrene',
  aTerpineol: 'α-Terpineol',
  aEudesmol: 'α-Eudesmol',
  aHumulene: 'α-Humulene',
  aTerpinene: 'α-Terpinene',
  aPinene: 'α-Pinene',
  bCaryophyllene: 'β-Caryophyllene',
  bEudesmol: 'β-Eudesmol',
  bFarnesene: 'β-Farnesene',
  bHumulene: 'β-Humulene',
  bOcimene: 'β-Ocimene',
  bTerpinene: 'β-Terpinene',
  bMyrcene: 'β-Myrcene',
  bPinene: 'β-Pinene',
  camphene: 'Camphene',
  carene: 'Carene',
  caryophylleneOxide: 'Caryophyllene Oxide',
  aLimonene: 'α-Limonene',
  borneol: 'Borneol',
  cisNerolidol: 'cis-Nerolidol',
  cisOcimene: 'cis-Ocimene',
  dLimonene: 'd-Limonene',
  endoFenchylAlcohol: 'Fenchyl Alcohol',
  eucalyptol: 'Eucalyptol',
  farnesol: 'Farnesol',
  fenchol: 'Fenchol',
  geraniol: 'Geraniol',
  gTerpinene: 'γ-Terpinene',
  guaiol: 'Guaiol',
  limonene: 'Limonene',
  linalool: 'Linalool',
  myrcene: 'Myrcene',
  nerol: 'Nerol',
  nerolidol: 'Nerolidol',
  ocimene: 'Ocimene',
  ocimeneIsomer1: 'Ocimene Isomer 1',
  ocimeneIsomer2: 'Ocimene Isomer 2',
  pMentha15diene: 'p-Mentha-1,5-diene',
  sabinene: 'Sabinene',
  sabineneHydrate: 'Sabinene Hydrate',
  squalene: 'Squalene',
  terpineol: 'Terpineol',
  terpinolene: 'Terpinolene',
  transNerolidol: 'trans-Nerolidol',
  valencene: 'Valencene',
  otherTerpenes: 'Other Terpenes',
  totalTerpenes: 'Total Terpenes',
};

export const labOverrideKeys = {
  coaFileIds: { value: 'lab_coaFileIds', enabled: 'lab_coaFileIds_override' },
  batchNumber: { value: 'lab_batchNumber', enabled: 'lab_batchNumber_override' },
  strainName: { value: 'lab_strainName', enabled: 'lab_strainName_override' },
  facility: { value: 'lab_facility', enabled: 'lab_facility_override' },
  license: { value: 'lab_license', enabled: 'lab_license_override' },
  testedBy: { value: 'lab_testedBy', enabled: 'lab_testedBy_override' },
  testedDate: { value: 'lab_testedDate', enabled: 'lab_testedDate_override' },
  cultivationDate: { value: 'lab_cultivationDate', enabled: 'lab_cultivationDate_override' },
  harvestDate: { value: 'lab_harvestDate', enabled: 'lab_harvestDate_override' },
  packagedDate: { value: 'lab_packagedDate', enabled: 'lab_packagedDate_override' },
  expirationDate: { value: 'lab_expirationDate', enabled: 'lab_expirationDate_override' },
  useByDate: { value: 'lab_useByDate', enabled: 'lab_useByDate_override' },
  sellByDate: { value: 'lab_sellByDate', enabled: 'lab_sellByDate_override' },
  laboratoryLicense: { value: 'lab_laboratoryLicense', enabled: 'lab_laboratoryLicense_override' },
  totalCannabinoids: { value: 'lab_totalCannabinoids', enabled: 'lab_totalCannabinoids_override' },
} as const;

const replaceGreek = (s: string) =>
  s
    .replace(/^a([A-Z])/, 'alpha$1')
    .replace(/^b([A-Z])/, 'beta$1')
    .replace(/^g([A-Z])/, 'gamma$1')
    .replace(/α/gi, 'alpha')
    .replace(/β/gi, 'beta')
    .replace(/γ/gi, 'gamma');

const slug = (s: string) =>
  replaceGreek(String(s || ''))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const cannabinoidOverrideKey = (name: string) => {
  let id = slug(name);
  if (id === 'totalcannabinoids') {
    id = 'totalCannabinoids';
  }

  return {
    enabled: `lab_cb_${id}_override`,
    weight: `lab_cb_${id}_weightMgG`,
    weightPkg: `lab_cb_${id}_weightPkg`,
    weightSrv: `lab_cb_${id}_weightSrv`,
    uom: `lab_cb_${id}_uom`,
    percent: `lab_cb_${id}_percent`,
  };
};

export const terpeneOverrideKey = (name: string) => {
  let id = slug(name);

  if (id === 'totalterpenes') {
    id = 'total_terpenes';
  }
  return {
    enabled: `lab_tp_${id}_override`,
    weight: `lab_tp_${id}_weightMgG`,
    percent: `lab_tp_${id}_percent`,
  };
};

const totalMassNumerators = new Set([...UNITS_OF_MEASURE.filter(unit => unit.QuantityType === 'WeightBased').map(unit => unit.Abbreviation.toLowerCase()), 'µg', 'μg']);

export const computeTotalCannabinoidDefaults = (
  cannabinoids: Record<string, { enabled?: boolean; percent?: number | string; weight?: number | string; basis?: LabWeightBasis; uom?: unknown; present?: boolean }>,
): { percent: number; weight?: number; basis?: LabWeightBasis; uom?: string } => {
  const derivativeKeys = new Set([
    'totalCannabinoids', 'totalActiveCannabinoids', 'totalThc', 'totalCbd', 'totalDelta9thc',
  ]);
  let percent = 0;
  let weight = 0;
  let contributors = 0;
  let hasComponentInput = false;
  let compatible = true;
  let dimension: { basis: LabWeightBasis; numerator: string; uom: string } | undefined;

  Object.entries(cannabinoids).forEach(([key, value]) => {
    if (!value || derivativeKeys.has(key)) return;
    // Preserve the established component/percent policy, including disabled rows.
    const cannabinoidPercent = Number(value.percent);
    if (!Number.isNaN(cannabinoidPercent)) percent += cannabinoidPercent;
    if (value.present || value.percent !== undefined || value.weight !== undefined) hasComponentInput = true;
    const measurement = parseLabMeasurement(value.weight);
    if (measurement === undefined) {
      if (value.present || value.percent !== undefined || value.weight !== undefined) compatible = false;
      return;
    }
    contributors += 1;
    const basis = validLabWeightBasis(value.basis);
    // Validate original resolved source metadata, not a selector's inherited mg default.
    const unit = typeof value.uom === 'string' ? /^([^/]+)(?:\/(g|ml|unit|srv|serving|pkg|package))?$/i.exec(value.uom) : null;
    if (!basis || !unit || !totalMassNumerators.has(unit[1].toLowerCase())) { compatible = false; return; }
    const numerator = unit[1].toLowerCase();
    if (dimension && (dimension.basis !== basis || dimension.numerator !== numerator)) compatible = false;
    const sourceDenominator = unit[2];
    const matching = sourceDenominator && inferredEditorLabWeightBasis({ unit: `mg/${sourceDenominator}` }) === basis;
    dimension ??= { basis, numerator, uom: `${unit[1]}/${matching ? sourceDenominator : labWeightDenominator[basis]}` };
    weight += measurement;
  });
  const totalPercent = Math.round(percent * 100) / 100;
  // The legacy empty form starts at zero; a nonempty unknown-dimensional result does not.
  if (!hasComponentInput) return { percent: totalPercent, weight: 0, basis: 'amount' as LabWeightBasis, uom: 'mg' };
  if (!contributors || !compatible || !dimension) return { percent: totalPercent, weight: undefined };
  return { percent: totalPercent, weight: Math.round(weight * 100) / 100, basis: dimension.basis, uom: dimension.uom };
};

type Props = {
  form: any;
  coa?: Record<string, unknown>;
  manualOverrides?: Record<string, unknown>;
  measurementsReadOnly?: boolean;
  setValue: (name: string, value: any) => void;
  totalCannabinoidsDraft?: TotalCannabinoidsDraft | null;
  onTotalCannabinoidsDraftChange?: (draft: TotalCannabinoidsDraft) => void;
  onRestoreTotalCannabinoidsDraft?: (draft: TotalCannabinoidsDraft) => void;
  onResetTotalCannabinoidsCalculation?: () => void;
  organizationId?: number | string;
  onSelectCoaOverrideFiles?: (files: File[]) => void;
  coaFiles?: string[];
  coaFileIds?: Array<number | string>;
  filePickerContextKey?: string;
  coaFileDisplayNames?: string[];
  pendingCoaFiles?: File[];
  pendingCoaFileDates?: Record<string, string>;
  onCoaFileDateChange?: (fileName: string, date: string) => void;
};

export type TotalCannabinoidsDraft = {
  percent: unknown;
  weight: unknown;
  uom: unknown;
  basis?: unknown;
  physicalValue?: unknown;
  physicalFieldPresent?: boolean;
  formFields?: Record<string, unknown>;
  manualFields?: Record<string, unknown>;
};

function useSearchDropdown() {
  const [open, setOpen] = React.useState(false);
  const blurTimeout = React.useRef<number | null>(null);
  const cancelPendingClose = React.useCallback(() => {
    if (blurTimeout.current !== null) {
      window.clearTimeout(blurTimeout.current);
      blurTimeout.current = null;
    }
  }, []);
  React.useEffect(() => cancelPendingClose, [cancelPendingClose]);

  const show = () => {
    // Refocusing must not inherit an earlier blur's delayed close.
    cancelPendingClose();
    setOpen(true);
  };
  const hide = () => {
    cancelPendingClose();
    setOpen(false);
  };
  const scheduleClose = () => {
    cancelPendingClose();
    blurTimeout.current = window.setTimeout(() => {
      blurTimeout.current = null;
      setOpen(false);
    }, 120);
  };
  return { open, show, hide, scheduleClose };
}

function Section({ title, testId, defaultOpen = true, readOnly = false, children }: any) {
  const [open, setOpen] = React.useState(Boolean(defaultOpen));
  return (
    <div style={{ marginTop: 12 }} data-testid={testId}>
      <div className="collapse-header" style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 800, color: 'var(--primary)' }}>{title}</div>
        <button type="button" className="subtle-btn" onClick={() => setOpen((v: boolean) => !v)} data-testid={`${testId}-toggle`}>
          {open ? 'Hide' : readOnly ? 'View' : 'Edit'}
        </button>
      </div>
      {open && <div data-testid={`${testId}-content`}>{children}</div>}
    </div>
  );
}

function OverrideRow({ checked, onToggle, label, testId, disabled = false, children }: any) {
  return (
    <div
      data-testid={testId}
      style={{
        display: 'grid',
        gridTemplateColumns: '22px 1fr',
        gap: 10,
        alignItems: 'center',
        padding: '8px 0',
      }}
    >
      <input
        type="checkbox"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onToggle(e.target.checked)}
        data-testid={`${testId}-enabled`}
      />
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ fontWeight: 800, color: 'var(--primary)', fontSize: 12 }}>{label}</div>
        {children}
      </div>
    </div>
  );
}

export default function PackageLabResultsOverrides({
  form,
  coa,
  manualOverrides,
  measurementsReadOnly = false,
  setValue,
  totalCannabinoidsDraft = null,
  onTotalCannabinoidsDraftChange,
  onRestoreTotalCannabinoidsDraft,
  onResetTotalCannabinoidsCalculation,
  organizationId,
  onSelectCoaOverrideFiles,
  coaFiles,
  coaFileIds,
  filePickerContextKey,
  coaFileDisplayNames,
  pendingCoaFiles,
  pendingCoaFileDates,
  onCoaFileDateChange,
}: Props) {
  const canUploadFiles = Boolean(onSelectCoaOverrideFiles && organizationId != null);
  const setNumber = (name: string, raw: string, opts?: { min?: number; max?: number }) => {
    if (raw === '') return setValue(name, '');
    const num = Number(raw);
    if (Number.isNaN(num)) return setValue(name, '');

    const min = typeof opts?.min === 'number' ? opts.min : undefined;
    const max = typeof opts?.max === 'number' ? opts.max : undefined;
    let clamped = num;
    if (min !== undefined) clamped = Math.max(min, clamped);
    if (max !== undefined) clamped = Math.min(max, clamped);
    return setValue(name, clamped);
  };

  const [cannabinoidSearch, setCannabinoidSearch] = React.useState('');
  const cannabinoidDropdown = useSearchDropdown();
  const cannabinoidInputRef = React.useRef<HTMLInputElement | null>(null);

  const cannabinoidOptions = React.useMemo(
    () =>
      (CANNABINOIDS as readonly string[]).map((key) => ({
        key,
        label: CANNABINOID_LABELS[String(key)] || String(key),
      })),
    [],
  );

  const selectedCannabinoidKeys = React.useMemo(
    () =>
      new Set(
        (cannabinoidOptions || [])
          .filter((opt) => Boolean(form?.[cannabinoidOverrideKey(opt.key).enabled]))
          .map((opt) => opt.key),
      ),
    [cannabinoidOptions, form],
  );

  const visibleCannabinoidOptions = React.useMemo(() => {
    const q = String(cannabinoidSearch || '')
      .trim()
      .toLowerCase();
    return (cannabinoidOptions || [])
      .filter((opt) => !selectedCannabinoidKeys.has(opt.key))
      .filter((opt) => {
        if (!q) return true;
        return (
          String(opt.label).toLowerCase().includes(q) || String(opt.key).toLowerCase().includes(q)
        );
      });
  }, [cannabinoidOptions, cannabinoidSearch, selectedCannabinoidKeys]);

  const addCannabinoid = (key: string) => {
    const keys = cannabinoidOverrideKey(key);
    if (key === 'totalCannabinoids' && totalCannabinoidsDraft && onRestoreTotalCannabinoidsDraft) {
      onRestoreTotalCannabinoidsDraft(totalCannabinoidsDraft);
      setCannabinoidSearch('');
      cannabinoidDropdown.hide();
      cannabinoidInputRef.current?.blur();
      return;
    }
    if (key === 'totalCannabinoids') onResetTotalCannabinoidsCalculation?.();
    setValue(keys.enabled, true);

    // When adding Total Cannabinoids, auto-sum individual cannabinoid percents and weights
    let initialPercent = 0;
    let initialWeight: number | undefined = 0;
    let initialBasis: LabWeightBasis | undefined;
    let initialUom: unknown = 'mg';
    if (key === 'totalCannabinoids') {
      if (totalCannabinoidsDraft) {
        setNumber(keys.weight, String(totalCannabinoidsDraft.weight ?? ''));
        setValue(keys.uom, totalCannabinoidsDraft.uom ?? 'mg');
        setNumber(keys.percent, String(totalCannabinoidsDraft.percent ?? ''), { min: 0, max: 100 });
      } else {
        const sourceFamilies = new Set(Object.keys(coa?.ingredients ?? {}).map(sourceKey =>
          manualCompoundField(cannabinoidOverrideKey(sourceKey).enabled)?.family));
        const defaults = computeTotalCannabinoidDefaults(
          Object.fromEntries(
            (CANNABINOIDS as readonly string[]).map((cannabinoid) => {
              const cannabinoidKeys = cannabinoidOverrideKey(cannabinoid);
              // Removal clears the controls; a retained COA family is no contribution.
              // Disabled rows with actual values retain the existing component policy.
              if (form?.[cannabinoidKeys.enabled] === false
                && [form?.[cannabinoidKeys.percent], form?.[cannabinoidKeys.weight]]
                  .every(value => value === undefined || value === null || value === '')) return [cannabinoid, {}];
              const compound = manualCompoundValue(coa?.ingredients, cannabinoid, cannabinoidKeys.enabled);
              const authoredPercent = hasManualCompoundField(manualOverrides, cannabinoidKeys.enabled, 'percent');
              const percent = authoredPercent
                ? manualCompoundFieldValue(manualOverrides, cannabinoidKeys.enabled, 'percent')
                : coa != null
                  ? selectLabPercent(compound)
                  : manualCompoundFieldValue(form, cannabinoidKeys.enabled, 'percent');
              const weight = projectManualLabWeight(compoundLabWeight(compound), manualOverrides, cannabinoidKeys.enabled);
              const basis = manualLabWeightBasis(weight, manualOverrides, cannabinoidKeys.enabled) ?? inferredEditorLabWeightBasis(weight);
              return [cannabinoid, {
                enabled: form?.[cannabinoidKeys.enabled],
                percent: parseLabMeasurement(percent),
                // Display may borrow another field; a calculated total cannot.
                weight: coa ? parseLabMeasurement(weight[basis]) : form?.[cannabinoidKeys.weight],
                present: sourceFamilies.has(manualCompoundField(cannabinoidKeys.enabled)?.family)
                  || typeof manualOverrideState(manualOverrides, cannabinoidKeys.enabled) === 'boolean',
                basis,
                uom: !coa || hasManualCompoundField(manualOverrides, cannabinoidKeys.enabled, 'uom') ? form?.[cannabinoidKeys.uom] : weight.unit,
              }];
            }),
          ),
        );
        initialPercent = defaults.percent;
        initialWeight = defaults.weight;
        initialBasis = defaults.basis;
        initialUom = defaults.uom;
      }
    }

    if (key !== 'totalCannabinoids' || !totalCannabinoidsDraft) {
      if (key === 'totalCannabinoids' && !onResetTotalCannabinoidsCalculation) {
        const prefix = keys.enabled.replace(/_override$/, '');
        const retainedFields = ['weightMgG', 'weightMl', 'weightPerUnit', 'weightPkg', 'weightSrv', 'weightBasis', 'uom'];
        if (retainedFields.some(field => form?.[`${prefix}_${field}`] !== undefined
          || manualCompoundFieldValue(manualOverrides, keys.enabled, field) !== undefined)) {
          // A fresh calculation must not revive measurements from a previously disabled row.
          for (const field of retainedFields) setValue(`${prefix}_${field}`, field === 'weightBasis' ? undefined : '');
        }
      }
      if (initialWeight !== undefined) {
        if (initialBasis) setValue(keys.enabled.replace(/_override$/, '_weightBasis'), initialBasis);
        setNumber(keys.weight, String(initialWeight));
        setValue(keys.uom, initialUom);
      }
      setNumber(keys.percent, String(initialPercent), { min: 0, max: 100 });
    }
    setCannabinoidSearch('');
    // Close the dropdown after selection.
    cannabinoidDropdown.hide();
    cannabinoidInputRef.current?.blur();
  };

  const removeCannabinoid = (key: string) => {
    const keys = cannabinoidOverrideKey(key);
    if (key === 'totalCannabinoids') {
      onTotalCannabinoidsDraftChange?.({
        percent: form?.[keys.percent],
        weight: form?.[keys.weight],
        uom: form?.[keys.uom],
      });
    }
    setValue(keys.enabled, false);
    setNumber(keys.weight, '');
    setNumber(keys.weightPkg, '');
    setNumber(keys.weightSrv, '');
    setValue(keys.uom, 'mg');
    setNumber(keys.percent, '');
  };

  const changeAmountOption = (value: string) => {
    setValue('amountOptions', value);
  };

  const [terpeneSearch, setTerpeneSearch] = React.useState('');
  const terpeneDropdown = useSearchDropdown();
  const terpeneInputRef = React.useRef<HTMLInputElement | null>(null);

  const terpeneOptions = React.useMemo(
    () =>
      (TERPENES as readonly string[]).map((key) => ({ key, label: TERPENE_LABELS[key] || key })),
    [],
  );

  const selectedTerpeneKeys = React.useMemo(
    () =>
      new Set(
        (terpeneOptions || [])
          .filter((opt) => Boolean(form?.[terpeneOverrideKey(opt.key).enabled]))
          .map((opt) => opt.key),
      ),
    [terpeneOptions, form],
  );

  const visibleTerpeneOptions = React.useMemo(() => {
    const q = String(terpeneSearch || '')
      .trim()
      .toLowerCase();
    return (terpeneOptions || [])
      .filter((opt) => !selectedTerpeneKeys.has(opt.key))
      .filter((opt) => {
        if (!q) return true;
        return (
          String(opt.label).toLowerCase().includes(q) || String(opt.key).toLowerCase().includes(q)
        );
      });
  }, [terpeneOptions, terpeneSearch, selectedTerpeneKeys]);

  const addTerpene = (key: string) => {
    const keys = terpeneOverrideKey(key);
    setValue(keys.enabled, true);

    // When adding Total Terpenes, auto-sum individual terpene percents and weights already in the form
    let initialPercent = 0;
    let initialWeight = 0;
    if (key === 'totalTerpenes') {
      const DERIVATIVE_KEYS = ['totalTerpenes', 'otherTerpenes'];
      (TERPENES as readonly string[])
        .filter((t) => !DERIVATIVE_KEYS.includes(t))
        .forEach((t) => {
          const tKeys = terpeneOverrideKey(t);
          if (form?.[tKeys.enabled]) {
            const p = Number(form?.[tKeys.percent]);
            if (!Number.isNaN(p)) initialPercent += p;
            const w = Number(form?.[tKeys.weight]);
            if (!Number.isNaN(w)) initialWeight += w;
          }
        });
      initialPercent = Math.round(initialPercent * 100) / 100;
      initialWeight = Math.round(initialWeight * 100) / 100;
    }

    setNumber(keys.weight, String(initialWeight), { min: 0 });
    setNumber(keys.percent, String(initialPercent), { min: 0, max: 100 });
    setTerpeneSearch('');
    terpeneDropdown.hide();
    terpeneInputRef.current?.blur();
  };

  const removeTerpene = (key: string) => {
    const keys = terpeneOverrideKey(key);
    setValue(keys.enabled, false);
    setNumber(keys.weight, '');
    setNumber(keys.percent, '');
  };

  const coaFileNames = React.useMemo(() => {
    if (!form?.[labOverrideKeys.coaFileIds.enabled]) return '';
    // Derive the label from this editor's current files. Sticky local names
    // otherwise survive package/organization changes and cleared uploads.
    const pendingNames = (pendingCoaFiles || []).map((file) => file.name);
    const withPending = (names: string[]) => [...names, ...pendingNames].join(', ');
    const selection = form?.[labOverrideKeys.coaFileIds.value];
    if (Array.isArray(selection) && selection.length === 0) return withPending([]);
    const selectedIds = Array.isArray(selection) ? new Set(selection.map(String)) : null;
    const isSelected = (index: number) =>
      !selectedIds || !coaFileIds || selectedIds.has(String(coaFileIds[index]));

    // Prefer server-provided display names (original filenames stored in the DB).
    // Filter out entries that are empty or the legacy placeholder value "file"
    // (caused by a prior bug that stored the form field name instead of the filename).
    const selectedIndexes = Array.from(
      { length: Math.max(coaFileDisplayNames?.length || 0, coaFiles?.length || 0) },
      (_, index) => index,
    ).filter(isSelected);
    const names = selectedIndexes
      .map((index) => {
        const name = typeof coaFileDisplayNames?.[index] === 'string' ? coaFileDisplayNames[index].trim() : '';
        if (name && name !== 'file') return name;
        // Resolve fallback per selected file, not only when every server name
        // is missing: mixed legacy/current records must retain both labels.
        try {
          const pathname = new URL(coaFiles?.[index] || '').pathname;
          const filename = pathname.split('/').pop();
          if (!filename) return null;
          const decoded = decodeURIComponent(filename);
          // Skip UUID/hash-like blob names (no file extension)
          if (!decoded.includes('.')) return null;
          return decoded;
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    if (names.length > 0) {
      return withPending(names);
    }
    const selectedUrls = selectedIndexes.map(index => coaFiles?.[index]).filter(Boolean);
    if (selectedUrls.length === 0) return withPending([]);
    return withPending([`${selectedUrls.length} COA file${selectedUrls.length > 1 ? 's' : ''} uploaded`]);
  }, [coaFiles, coaFileIds, coaFileDisplayNames, pendingCoaFiles, form?.[labOverrideKeys.coaFileIds.enabled], form?.[labOverrideKeys.coaFileIds.value]]);

  return (
    <div className="card" style={{ padding: 14 }} data-testid="package-lab-results-overrides">
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>Lab Results Overrides</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        Check a box to override that value in the preview.
      </div>

      {measurementsReadOnly && (
        <p className="muted" data-testid="package-lab-results-source-readonly" role="note">
          Authoritative laboratory measurements, lab identity, and test date are read-only. Relink Test Results to refresh them from the source.
        </p>
      )}

      <Section title="Fields" testId="package-lab-results-fields" defaultOpen={false}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px' }}>
          {canUploadFiles && (
            <OverrideRow
              label="COA Files"
              testId="package-lab-results-coa-files"
              checked={form?.[labOverrideKeys.coaFileIds.enabled]}
              onToggle={(v) => setValue(labOverrideKeys.coaFileIds.enabled, v)}
            >
              <FileField
                key={`${filePickerContextKey}:${Boolean(form?.[labOverrideKeys.coaFileIds.enabled])}`}
                label={<></>}
                accept=".pdf, application/pdf"
                multiple={true}
                disabled={!form?.[labOverrideKeys.coaFileIds.enabled]}
                selectedName={coaFileNames}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const files = event.target.files;
                  if (files && onSelectCoaOverrideFiles && organizationId != null) {
                    const selectedFiles = Array.from(files);
                    // Auto-enable the override as soon as files are selected, but defer upload until save.
                    setValue(labOverrideKeys.coaFileIds.enabled, true);
                    // Save adds this picker batch to the retained server-file
                    // selection, preserving explicit removals/subsets. Picking
                    // again replaces the unsaved batch (normal FileList behavior).
                    onSelectCoaOverrideFiles(selectedFiles);
                  }
                }}
              />
              {pendingCoaFiles && pendingCoaFiles.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Test Performed Date</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {pendingCoaFiles.map((file) => (
                      <div key={file.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <span style={{ minWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </span>
                        <input
                          type="date"
                          className="input"
                          style={{ width: 160, fontSize: 12 }}
                          value={pendingCoaFileDates?.[file.name] || ''}
                          onChange={(e) => onCoaFileDateChange?.(file.name, e.target.value)}
                          required
                          data-testid={`package-lab-results-coa-file-date-${file.name}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </OverrideRow>
          )}

          {canUploadFiles && <div className="divider" style={{ margin: '10px 0' }} />}

          <OverrideRow
            label="Batch"
            testId="package-lab-results-batch"
            checked={form?.[labOverrideKeys.batchNumber.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.batchNumber.enabled, v)}
          >
            <input
              className="input"
              value={form?.[labOverrideKeys.batchNumber.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.batchNumber.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.batchNumber.enabled]}
              data-testid="package-lab-results-batch-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Strain"
            testId="package-lab-results-strain"
            checked={form?.[labOverrideKeys.strainName.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.strainName.enabled, v)}
          >
            <input
              className="input"
              value={form?.[labOverrideKeys.strainName.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.strainName.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.strainName.enabled]}
              data-testid="package-lab-results-strain-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Facility"
            testId="package-lab-results-facility"
            checked={form?.[labOverrideKeys.facility.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.facility.enabled, v)}
          >
            <input
              className="input"
              value={form?.[labOverrideKeys.facility.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.facility.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.facility.enabled]}
              data-testid="package-lab-results-facility-input"
            />
          </OverrideRow>

          <OverrideRow
            label="License"
            testId="package-lab-results-license"
            checked={form?.[labOverrideKeys.license.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.license.enabled, v)}
          >
            <input
              className="input"
              value={form?.[labOverrideKeys.license.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.license.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.license.enabled]}
              data-testid="package-lab-results-license-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Tested By"
            testId="package-lab-results-tested-by"
            disabled={measurementsReadOnly}
            checked={form?.[labOverrideKeys.testedBy.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.testedBy.enabled, v)}
          >
            <input
              className="input"
              value={form?.[labOverrideKeys.testedBy.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.testedBy.value, e.target.value)}
              disabled={measurementsReadOnly || !form?.[labOverrideKeys.testedBy.enabled]}
              data-testid="package-lab-results-tested-by-input"
            />
          </OverrideRow>
          <OverrideRow
            label="Tested Date"
            testId="package-lab-results-tested-date"
            disabled={measurementsReadOnly}
            checked={form?.[labOverrideKeys.testedDate.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.testedDate.enabled, v)}
          >
            <input
              type="date"
              className="input"
              value={form?.[labOverrideKeys.testedDate.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.testedDate.value, e.target.value)}
              disabled={measurementsReadOnly || !form?.[labOverrideKeys.testedDate.enabled]}
              data-testid="package-lab-results-tested-date-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Harvest Date"
            testId="package-lab-results-harvest-date"
            checked={form?.[labOverrideKeys.harvestDate.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.harvestDate.enabled, v)}
          >
            <input
              type="date"
              className="input"
              value={form?.[labOverrideKeys.harvestDate.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.harvestDate.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.harvestDate.enabled]}
              data-testid="package-lab-results-harvest-date-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Packaged Date"
            testId="package-lab-results-packaged-date"
            checked={form?.[labOverrideKeys.packagedDate.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.packagedDate.enabled, v)}
          >
            <input
              type="date"
              className="input"
              value={form?.[labOverrideKeys.packagedDate.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.packagedDate.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.packagedDate.enabled]}
              data-testid="package-lab-results-packaged-date-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Expiration Date"
            testId="package-lab-results-expiration-date"
            checked={form?.[labOverrideKeys.expirationDate.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.expirationDate.enabled, v)}
          >
            <input
              type="date"
              className="input"
              value={form?.[labOverrideKeys.expirationDate.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.expirationDate.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.expirationDate.enabled]}
              data-testid="package-lab-results-expiration-date-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Use By Date"
            testId="package-lab-results-use-by-date"
            checked={form?.[labOverrideKeys.useByDate.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.useByDate.enabled, v)}
          >
            <input
              type="date"
              className="input"
              value={form?.[labOverrideKeys.useByDate.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.useByDate.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.useByDate.enabled]}
              data-testid="package-lab-results-use-by-date-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Sell By Date"
            testId="package-lab-results-sell-by-date"
            checked={form?.[labOverrideKeys.sellByDate.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.sellByDate.enabled, v)}
          >
            <input
              type="date"
              className="input"
              value={form?.[labOverrideKeys.sellByDate.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.sellByDate.value, e.target.value)}
              disabled={!form?.[labOverrideKeys.sellByDate.enabled]}
              data-testid="package-lab-results-sell-by-date-input"
            />
          </OverrideRow>

          <OverrideRow
            label="Laboratory License"
            testId="package-lab-results-laboratory-license"
            disabled={measurementsReadOnly}
            checked={form?.[labOverrideKeys.laboratoryLicense.enabled]}
            onToggle={(v) => setValue(labOverrideKeys.laboratoryLicense.enabled, v)}
          >
            <input
              className="input"
              value={form?.[labOverrideKeys.laboratoryLicense.value] ?? ''}
              onChange={(e) => setValue(labOverrideKeys.laboratoryLicense.value, e.target.value)}
              disabled={measurementsReadOnly || !form?.[labOverrideKeys.laboratoryLicense.enabled]}
              data-testid="package-lab-results-laboratory-license-input"
            />
          </OverrideRow>
        </div>
      </Section>

      <Section title="Cannabinoids" testId="package-lab-results-cannabinoids" defaultOpen={false} readOnly={measurementsReadOnly}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 260px',
              gap: 16,
              alignItems: 'start',
              marginBottom: 8,
            }}
          >
            <div style={{ position: 'relative' }}>
              <input
                ref={cannabinoidInputRef}
                className="input"
                placeholder="Cannabinoids…."
                value={cannabinoidSearch}
                disabled={measurementsReadOnly}
                data-testid="package-lab-results-cannabinoid-search"
                onChange={(e) => {
                  setCannabinoidSearch(e.target.value);
                  cannabinoidDropdown.show();
                }}
                onFocus={cannabinoidDropdown.show}
                onBlur={cannabinoidDropdown.scheduleClose}
              />
              {selectedCannabinoidKeys.size === 0 && (
                <div className="muted" style={{ fontSize: 12 }}>
                  Search above to add cannabinoids.
                </div>
              )}

              {cannabinoidDropdown.open && visibleCannabinoidOptions.length > 0 && (
                <div
                  data-testid="package-lab-results-cannabinoid-options"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 'calc(100% + 22px)',
                    zIndex: 5,
                    background: 'var(--panel)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    maxHeight: 240,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  }}
                >
                  {visibleCannabinoidOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => addCannabinoid(opt.key)}
                      disabled={measurementsReadOnly}
                      data-testid={`package-lab-results-cannabinoid-option-${opt.key}`}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        border: 'none',
                        borderBottom: '1px solid var(--border)',
                        background: 'var(--panel)',
                        cursor: 'pointer',
                        color: 'var(--primary)',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <InputRadioButton
              options={AMOUNT_OPTIONS.map((o) => {
                const current = form?.amountOptions;
                const isSelected = current ? current === o : o === 'Both';
                return { name: o, value: o, selected: isSelected };
              })}
              title={'Display cannabinoids by:'}
              onChange={changeAmountOption}
              testId="package-lab-results-amount-options"
            ></InputRadioButton>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {(cannabinoidOptions || [])
              .filter((opt) => selectedCannabinoidKeys.has(opt.key))
              .map((opt) => {
                const keys = cannabinoidOverrideKey(opt.key);
                const uomValue = String(form?.[keys.uom] ?? 'mg').split('/')[0];
                const weight = projectManualLabWeight(compoundLabWeight(manualCompoundValue(coa?.ingredients, opt.key, keys.enabled)), manualOverrides, keys.enabled);
                const basis = manualLabWeightBasis(weight, manualOverrides, keys.enabled) ?? inferredEditorLabWeightBasis(weight);
                return (
                  <div
                    key={opt.key}
                    data-testid={`package-lab-results-cannabinoid-${opt.key}`}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: 12,
                      display: 'grid',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ fontWeight: 900, fontSize: 18, color: 'var(--primary)' }}>
                        {opt.label}
                      </div>
                      <button
                        type="button"
                        className="subtle-btn"
                        onClick={() => removeCannabinoid(opt.key)}
                        aria-label={`Remove ${opt.label}`}
                        title="Remove"
                        style={{ color: 'var(--danger)', fontWeight: 900 }}
                        disabled={measurementsReadOnly}
                        data-testid={`package-lab-results-cannabinoid-remove-${opt.key}`}
                      >
                        <TrashActionIcon className="trash-icon-lg" />
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <div className="muted" style={{ fontSize: 12, fontWeight: 800 }}>
                          Weight <span data-testid={`package-lab-results-cannabinoid-basis-${opt.key}`}>per {labWeightDenominator[basis]}</span>
                        </div>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          step="0.001"
                          min="0"
                          placeholder="Weight"
                          style={{ width: 160 }}
                          value={form?.[keys.weight] ?? ''}
                          onChange={(e) => setNumber(keys.weight, e.target.value, { min: 0 })}
                          disabled={measurementsReadOnly}
                          data-testid={`package-lab-results-cannabinoid-weight-${opt.key}`}
                        />
                      </div>

                      <div style={{ display: 'grid', gap: 6 }}>
                        <div className="muted" style={{ fontSize: 12, fontWeight: 800 }}>
                          UOM
                        </div>
                        <select
                          className="select"
                          style={{ width: 140 }}
                          value={uomValue}
                          onChange={(e) => setValue(keys.uom, e.target.value)}
                          disabled={measurementsReadOnly}
                          data-testid={`package-lab-results-cannabinoid-uom-${opt.key}`}
                        >
                          {UNITS_OF_MEASURE_OPTIONS.map((u) => (
                            <option key={u.value} value={u.value}>
                              {u.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ display: 'grid', gap: 6 }}>
                        <div className="muted" style={{ fontSize: 12, fontWeight: 800 }}>
                          Percent
                        </div>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="Percent"
                          style={{ width: 140 }}
                          value={form?.[keys.percent] ?? ''}
                          onChange={(e) =>
                            setNumber(keys.percent, e.target.value, { min: 0, max: 100 })
                          }
                          disabled={measurementsReadOnly}
                          data-testid={`package-lab-results-cannabinoid-percent-${opt.key}`}
                        />
                      </div>

                      <div style={{ display: 'grid', gap: 6 }}>
                        <div className="muted" style={{ fontSize: 12, fontWeight: 800 }}>
                          Per Package
                        </div>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          step="0.001"
                          min="0"
                          placeholder="Per Pkg"
                          style={{ width: 140 }}
                          value={form?.[keys.weightPkg] ?? ''}
                          onChange={(e) => setNumber(keys.weightPkg, e.target.value, { min: 0 })}
                          disabled={measurementsReadOnly}
                          data-testid={`package-lab-results-cannabinoid-per-package-${opt.key}`}
                        />
                      </div>

                      <div style={{ display: 'grid', gap: 6 }}>
                        <div className="muted" style={{ fontSize: 12, fontWeight: 800 }}>
                          Per Serving
                        </div>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          step="0.001"
                          min="0"
                          placeholder="Per Srv"
                          style={{ width: 140 }}
                          value={form?.[keys.weightSrv] ?? ''}
                          onChange={(e) => setNumber(keys.weightSrv, e.target.value, { min: 0 })}
                          disabled={measurementsReadOnly}
                          data-testid={`package-lab-results-cannabinoid-per-serving-${opt.key}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </Section>


      <Section title="Terpenes" testId="package-lab-results-terpenes" defaultOpen={false} readOnly={measurementsReadOnly}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px' }}>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input
              ref={terpeneInputRef}
              className="input"
              placeholder="Terpenes…."
              value={terpeneSearch}
              disabled={measurementsReadOnly}
              data-testid="package-lab-results-terpene-search"
              onChange={(e) => {
                setTerpeneSearch(e.target.value);
                terpeneDropdown.show();
              }}
              onFocus={terpeneDropdown.show}
              onBlur={terpeneDropdown.scheduleClose}
            />

            {terpeneDropdown.open && visibleTerpeneOptions.length > 0 && (
              <div
                data-testid="package-lab-results-terpene-options"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 'calc(100% + 6px)',
                  zIndex: 5,
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  maxHeight: 240,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                }}
              >
                {visibleTerpeneOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addTerpene(opt.key)}
                    disabled={measurementsReadOnly}
                    data-testid={`package-lab-results-terpene-option-${opt.key}`}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 'none',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--panel)',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {(terpeneOptions || [])
              .filter((opt) => selectedTerpeneKeys.has(opt.key))
              .map((opt) => {
                const keys = terpeneOverrideKey(opt.key);
                return (
                  <div
                    key={opt.key}
                    data-testid={`package-lab-results-terpene-${opt.key}`}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: 12,
                      display: 'grid',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ fontWeight: 900, fontSize: 18, color: 'var(--primary)' }}>
                        {opt.label}
                      </div>
                      <button
                        type="button"
                        className="subtle-btn"
                        onClick={() => removeTerpene(opt.key)}
                        aria-label={`Remove ${opt.label}`}
                        title="Remove"
                        style={{ color: 'var(--danger)', fontWeight: 900 }}
                        disabled={measurementsReadOnly}
                        data-testid={`package-lab-results-terpene-remove-${opt.key}`}
                      >
                        <TrashActionIcon className="trash-icon-lg" />
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <div className="muted" style={{ fontSize: 12, fontWeight: 800 }}>
                          Percent
                        </div>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="Percent"
                          style={{ width: 140 }}
                          value={form?.[keys.percent] ?? ''}
                          onChange={(e) =>
                            setNumber(keys.percent, e.target.value, { min: 0, max: 100 })
                          }
                          disabled={measurementsReadOnly}
                          data-testid={`package-lab-results-terpene-percent-${opt.key}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

            {selectedTerpeneKeys.size === 0 && (
              <div className="muted" style={{ fontSize: 12 }}>
                Search above to add terpenes.
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
